import {
  AreaType,
  AtGradeExpectedIntxTypes,
  AtGradeIntxTypes,
  CrashSeverity,
  SafetyIntxType,
} from "./SPFConstants";
import {
  RoundaboutSpfs,
  get3LegRoundaboutSPFs,
  get4LegRoundaboutSPFs,
  getExpectedCrashes,
  getFatalInjuryCrashRatio,
} from "./spfHelpers";
const getSPFsForIntxType = (intxType: SafetyIntxType) => {
  switch (intxType) {
    case SafetyIntxType.THREE_LEG_MINOR_STOP:
      return {
        getTotalPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-8.072) *
          Math.pow(majorRoad.aadt, 0.509) *
          Math.pow(minorRoad.aadt, 0.509),

        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-7.83) *
          Math.pow(majorRoad.aadt, 0.459) *
          Math.pow(minorRoad.aadt, 0.459),
      };
    case SafetyIntxType.FOUR_LEG_MINOR_STOP:
      return {
        getTotalPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-4.342) *
          Math.pow(majorRoad.aadt, 0.334) *
          Math.pow(minorRoad.aadt, 0.264),

        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-3.248) *
          Math.pow(majorRoad.aadt, 0.217) *
          Math.pow(minorRoad.aadt, 0.152),
      };
    case SafetyIntxType.THREE_LEG_SIGNAL:
    case SafetyIntxType.FOUR_LEG_SIGNAL:
      return {
        getTotalPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-3.563) *
          Math.pow(majorRoad.aadt, 0.389) *
          Math.pow(minorRoad.aadt, 0.134),
        getFatalInjuryPredicted: ({ majorRoad, minorRoad }: AtGradeIntxTypes) =>
          Math.exp(-3.301) *
          Math.pow(majorRoad.aadt, 0.291) *
          Math.pow(minorRoad.aadt, 0.133),
      };
    case SafetyIntxType.THREE_LEG_ROUNDABOUT:
      return get3LegRoundaboutSPFs(AreaType.RURAL);
    case SafetyIntxType.FOUR_LEG_ROUNDABOUT:
      return get4LegRoundaboutSPFs(AreaType.RURAL);
    default:
      throw new Error(`Cannot find SPFs for intersection type: ${intxType}`);
  }
};

const getFatalInjuryPredictedInitial = (props: AtGradeIntxTypes) => {
  return getSPFsForIntxType(props.intxType)?.getFatalInjuryPredicted(props);
};
const getPdoPredictedInitial = (props: AtGradeIntxTypes) => {
  const { getPDOPredicted } = getSPFsForIntxType(
    props.intxType
  ) as RoundaboutSpfs;
  if (!getPDOPredicted) {
    throw new Error("Could not find getPDOPredicted function");
  }
  return getPDOPredicted(props);
};

export const getFatalInjuryPredicted = (props: AtGradeIntxTypes): number => {
  if (
    props.intxType === SafetyIntxType.THREE_LEG_ROUNDABOUT ||
    props.intxType === SafetyIntxType.FOUR_LEG_ROUNDABOUT
  ) {
    const fiInitial = getFatalInjuryPredictedInitial(props);
    const totalPredicted = getTotalPredicted(props);
    const pdoInitial = getPdoPredictedInitial(props);

    return getFatalInjuryCrashRatio({
      fiInitial,
      pdoInitial,
      totalCrashes: totalPredicted,
    });
  }
  return getFatalInjuryPredictedInitial(props);
};
export const getTotalPredicted = (props: AtGradeIntxTypes): number => {
  return getSPFsForIntxType(props.intxType)?.getTotalPredicted(props);
};

export const getTotalExpected = (props: AtGradeExpectedIntxTypes): number =>
  getExpectedCrashes({
    ...props,
    crashSeverity: CrashSeverity.TOTAL,
    getPredictedCrashes: getTotalPredicted,
  });

export const getFatalInjuryExpected = (
  props: AtGradeExpectedIntxTypes
): number => {
  if (
    props.intxType === SafetyIntxType.THREE_LEG_ROUNDABOUT ||
    props.intxType === SafetyIntxType.FOUR_LEG_ROUNDABOUT
  ) {
    const fiInitial = getFatalInjuryPredictedInitial(props);
    const totalExpected = getTotalExpected(props);
    const pdoInitial = getPdoPredictedInitial(props);

    return getFatalInjuryCrashRatio({
      fiInitial,
      pdoInitial,
      totalCrashes: totalExpected,
    });
  }
  return getExpectedCrashes({
    ...props,
    crashSeverity: CrashSeverity.FATAL_INJURY,
    getPredictedCrashes: getFatalInjuryPredicted,
  });
};
